import React from "react";
import { Link } from "react-router-dom";
import FAQ from "../FAQ";
import Footer from "../Footer";
import "./HelpCenter.css";
import { ClockIcon, MailIcon } from "../public/icons";
import {
  formatSubscriptionPrice,
  getPublishedSubscriptionPlan,
} from "../../constants/subscriptionPlans";
import { SUPPORT_EMAIL, SUPPORT_REPLY_WINDOW } from "../../constants/supportContact";

const singlePrice = formatSubscriptionPrice(getPublishedSubscriptionPlan("single").price, "en-US");
const teamPrice = formatSubscriptionPrice(getPublishedSubscriptionPlan("team").price, "en-US");

export const helpTopics = [
  {
    id: "help-payments",
    navLabel: "Payments & payouts",
    heading: "Payments & payouts",
    items: [
      {
        question: "How do I get paid?",
        answer:
          "Payments route to your connected Stripe account. In Team Mode the business owner chooses whether new booking payments go to the business account or the assigned provider — existing bookings keep their original recipient.",
      },
      {
        question: "When does the money land?",
        answer:
          "Stripe sets payout timing — most payments reach your bank within a few business days. Your first payout can take 7–14 days while Stripe verifies the account.",
      },
      {
        question: "What are the fees?",
        answer:
          "Haastia takes no cut of your bookings. We cover Stripe's processing, refund, and dispute costs.",
      },
      {
        question: "Do I need Stripe to accept bookings?",
        answer:
          "Not for free or in-person bookings. You need Stripe connected to charge deposits or take payment online.",
      },
      {
        question: "What happens if a client wants a refund?",
        answer:
          "Refunds follow your cancellation policy. If the booking qualifies, the refund goes back to the original payment method automatically. You can also send one by hand from the payment dashboard.",
      },
    ],
  },
  {
    id: "help-team",
    navLabel: "Team Mode",
    heading: "Team Mode",
    items: [
      {
        question: "What is Team Mode?",
        answer:
          "Team Mode gives your salon, suite, or service business one shared Haastia account for you and everyone who works with you. You manage providers, services, schedules, bookings, and where the money goes, all from one place.",
      },
      {
        question: "Who can invite and manage team members?",
        answer:
          "If you're the business owner or a business admin, you can send invites, chase pending ones, change roles, and assign services. Team members work with the services and bookings you assign them.",
      },
      {
        question: "What is the difference between a business admin and a team member?",
        answer:
          "A business admin helps you run the business and manage who has access. A team member is someone who takes bookings for the services you assign them. Only the business owner controls billing, ownership, and the owner-only Stripe settings.",
      },
      {
        question: "How do service assignments work in Team Mode?",
        answer:
          "As the owner or a business admin, you assign services to each team member. That keeps everyone connected to the work they actually do.",
      },
      {
        question: "How do payouts work in Team Mode?",
        answer:
          "You can have payouts land in the business Stripe account, or go straight to each provider's own Stripe account. Either way, a change to payment routing only affects new bookings.",
      },
      {
        question: "Can we switch between Single Mode and Team Mode?",
        answer:
          "The business owner can switch modes in billing and ownership settings. Before you can go back to Single Mode you may need to tidy a few things up first — active team members, service assignments, or bookings someone else is due to take.",
      },
      {
        question: "Does Haastia calculate team payroll or commissions?",
        answer:
          "No. Payment routing won't work out payroll, commissions, taxes, or what you pay your team — that stays with you.",
      },
    ],
  },
  {
    id: "help-booking",
    navLabel: "Booking & availability",
    heading: "Booking & availability",
    items: [
      {
        question: "How do I set my availability?",
        answer:
          "In the Availability section of your dashboard, pick the days and times you'll accept bookings. Nothing outside those windows is ever offered.",
      },
      {
        question: "Can I block off time or take days off?",
        answer: "Yes. Update your availability whenever you like to block specific days or hours.",
      },
      {
        question: "What happens when someone books me?",
        answer:
          "The appointment appears in your Bookings dashboard with every detail attached, and confirmation goes out to the client immediately.",
      },
      {
        question: "Do I have to accept each booking?",
        answer: "No — bookings confirm automatically against your availability, which is the point.",
      },
    ],
  },
  {
    id: "help-cancel",
    navLabel: "Cancellations",
    heading: "Cancellations & rescheduling",
    items: [
      {
        question: "How do cancellations work?",
        answer:
          "Your clients cancel from their own booking page, and refunds follow the policy you picked. You can also cancel a booking yourself and decide whether to refund it.",
      },
      {
        question: "Can I set my own policy?",
        answer:
          "Yes — flexible, moderate or strict, chosen in settings and shown to the client before they confirm.",
      },
      {
        question: "Can clients reschedule?",
        answer:
          "Yes, against your live availability — so a moved appointment can't land somewhere you're already busy.",
      },
    ],
  },
  {
    id: "help-page",
    navLabel: "Your page & services",
    heading: "Your page & services",
    items: [
      {
        question: "How do I customize my booking page?",
        answer:
          "Open Booking page in your dashboard's sidebar. Page content is where you add your profile picture, write your introduction and policies, and upload portfolio photos. Appearance sets your colors, fonts and layout. Visual editor lets you change the look while you watch your page update. Looking at your own booking page while signed in? The edit button takes you straight to the visual editor.",
      },
      {
        question: "How do I make my booking page look like the example?",
        answer: (
          <>
            The <Link to="/example">example booking page</Link> uses the Standard format. Open
            Booking page, choose Appearance, select Standard under Page format, then save. Your
            Custom sections stay saved, so you can switch back whenever you like.
          </>
        ),
      },
      {
        question: "How do I update or remove a service?",
        answer:
          "Open your Services section and edit or delete it there. Existing bookings for that service are unaffected.",
      },
      {
        question: "Can I save notes on my clients?",
        answer:
          "Yes. Notes and booking history sit on each client record, and returning clients are tracked automatically.",
      },
    ],
  },
  {
    id: "help-import",
    navLabel: "Moving from another app",
    heading: "Moving from another app",
    items: [
      {
        question: "Can I bring my clients and calendar over from another booking app?",
        answer: (
          <>
            Yes. Open <Link to="/pro/import">Switch to Haastia</Link> and pick the app you use today.
            Vagaro, Square Appointments, Fresha, GlossGenius and Booksy all export files you can
            upload, and we show you how to get them out of each one. On Acuity, your services come
            from your booking page link, and your clients and appointments from files you export. On
            any other app, a spreadsheet of your clients, services or appointments works just as well
            — you match the columns yourself in a step that takes a minute.
          </>
        ),
      },
      {
        question: "What comes across?",
        answer:
          "Clients with their names, contact details and any notes you kept on them; your services with their lengths and prices; and appointments, past and upcoming. You choose whether to bring history and cancelled visits over before anything is saved.",
      },
      {
        question: "I'm on Acuity. What does my booking page link bring over?",
        answer:
          "Paste the link you share with clients — it usually ends in .as.me — and we read your services straight off it, with their prices, lengths, descriptions, categories and photos. Your add-ons come across on the services that offer them, and the questions Acuity asks before booking become your booking questions. Acuity's page doesn't say how much deposit you take, so we ask you before anything is saved. Your page's colors, font and intro come across too, and we set your booking page to Custom. You can switch back to Standard in Appearance whenever you like. On the Switch to Haastia page, the guided move takes you from your link to going live in seven short steps, and you can stop and pick up where you left off.",
      },
      {
        question: "What doesn't come across from Acuity?",
        answer:
          "Your hours. Acuity doesn't show them on your booking page, so set them in Availability. Packages and gift certificates stay on Acuity, because Haastia doesn't sell them. Haastia asks up to 10 booking questions and has no lists of choices, so a dropdown becomes a typed answer, and an add-on over $500 or 4 hours stays behind. Whatever doesn't fit, and any photo we can't copy, is listed when the import finishes so you can add it by hand.",
      },
      {
        question: "Will my clients be emailed about it?",
        answer:
          "No. Nothing we import sends a confirmation, and nobody hears that you moved. Appointments still to come do get your usual Haastia reminder nearer the time, which is the point of bringing them over — and you can switch that off for the import if you'd rather they didn't.",
      },
      {
        question: "What happens to money and card details?",
        answer:
          "Neither comes across. Card details are never read, and a past appointment is never turned into a Haastia payment. What the old app charged is kept alongside the appointment for your reference only, so your takings and payouts here start from the day you switch.",
      },
      {
        question: "What if I get it wrong?",
        answer:
          "Nothing is saved until you've seen exactly what will be added — how many clients, services and appointments, and anything we couldn't read. Afterwards you can undo the whole import. Anything you or your clients have used since stays put, like an appointment that's taken a payment or one you've moved, and we tell you what we kept. Undoing an Acuity link import puts your page's colors, font, intro and format back too, unless you've changed them since.",
      },
    ],
  },
  {
    id: "help-account",
    navLabel: "Account & subscription",
    heading: "Account & subscription",
    items: [
      {
        question: "How much does Haastia cost?",
        answer: `${singlePrice} a month working on your own, ${teamPrice} a month for a shared team workspace.`,
      },
      {
        question: "How do I cancel or pause?",
        answer:
          "On your own, from account settings. In Team Mode the business owner manages it, and pausing isn't available until team dependencies are resolved.",
      },
      {
        question: "What happens to my bookings if I cancel?",
        answer: "They stay in place, but your profile stops taking new bookings.",
      },
      {
        question: "How do I delete my account, and what happens to my data?",
        answer: (
          <>
            Account settings has both options. Deactivating takes your booking page down and keeps
            everything; deleting is permanent and asks for your password. Past bookings and payment
            records are kept where we are required to hold them — the{" "}
            <Link to="/privacy#p15">privacy policy</Link> sets out exactly what goes and what stays.
          </>
        ),
      },
    ],
  },
  {
    id: "help-clients",
    navLabel: "For your clients",
    heading: "For your clients",
    intro: "Send them here if they ask — this is what they see.",
    variant: "surface",
    items: [
      {
        question: "Do I need an account to book?",
        answer: "No. You can book as a guest.",
      },
      {
        question: "When am I charged?",
        answer:
          "At the time of booking if you're paying online. Any tip is charged in full today, even when you're only paying a deposit.",
      },
      {
        question: "How do I manage or cancel my booking?",
        answer:
          "Use the link in your confirmation. You can view, reschedule or cancel from there at any time; refunds depend on the provider's policy.",
      },
      {
        question: "What does Haastia do with my details?",
        answer: (
          <>
            Your booking details go to the professional you booked with, and to Stripe if you pay
            online. Photos you upload answering booking questions are deleted about 48 hours after
            the appointment. The <Link to="/privacy">privacy policy</Link> has the full picture.
          </>
        ),
      },
    ],
  },
];

const HelpCenter = () => (
  <>
    <main className="help-page">
      <section className="site-section site-section--hero help-hero">
        <span className="deco deco--2 help-hero__deco" aria-hidden="true" />
        <div className="site-inner">
          <div className="help-hero__grid">
            <div>
              <p className="eyebrow">Support</p>
              <h1>Help center</h1>
              <p className="lead help-hero__lead">
                Answers for the things people write in about most. If yours isn't here, email us
                — a person reads it, and you'll hear back within {SUPPORT_REPLY_WINDOW}.
              </p>
              <div className="btn-row help-hero__actions">
                <a className="btn btn-primary btn-md" href={`mailto:${SUPPORT_EMAIL}`}>
                  <MailIcon size={16} />
                  Email support
                </a>
                <Link className="btn btn-secondary btn-md" to="/contact">
                  Send a message
                </Link>
              </div>
            </div>

            <aside className="panel help-status" aria-label="Support status">
              <div className="info-row">
                <span className="icon-circle icon-circle--sage">
                  <ClockIcon size={20} />
                </span>
                <div>
                  <p>Average reply time</p>
                  <strong>Within {SUPPORT_REPLY_WINDOW}</strong>
                </div>
              </div>
              <div className="help-status__divider" />
              <div className="info-row">
                <span className="icon-circle">
                  <MailIcon size={20} />
                </span>
                <div>
                  <p>{SUPPORT_EMAIL}</p>
                  <strong>Screenshots get you a faster answer</strong>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="site-section site-section--flush-top help-content">
        <div className="site-inner">
          <div className="help-layout">
            <nav className="help-topics" aria-label="Help topics">
              <p className="eyebrow eyebrow--muted">Topics</p>
              {helpTopics.map((topic) => (
                <a href={`#${topic.id}`} key={topic.id}>
                  {topic.navLabel}
                </a>
              ))}
            </nav>

            <div className="help-sections">
              {helpTopics.map((topic) => (
                <section id={topic.id} className="help-topic" key={topic.id}>
                  <h2>{topic.heading}</h2>
                  {topic.intro && <p className="help-topic__intro">{topic.intro}</p>}
                  <div className="faq-list">
                    {topic.items.map((faq) => (
                      <FAQ
                        key={faq.question}
                        question={faq.question}
                        answer={faq.answer}
                        variant={topic.variant}
                      />
                    ))}
                  </div>
                </section>
              ))}

              <div className="panel panel--surface help-cta">
                <div>
                  <h3>Still stuck?</h3>
                  <p>Average response time: {SUPPORT_REPLY_WINDOW}.</p>
                </div>
                <a className="btn btn-primary btn-md" href={`mailto:${SUPPORT_EMAIL}`}>
                  Email support
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

export default HelpCenter;
